import { useState } from "react";
import { toast } from "sonner";
import { Camera, Sparkle, X, Share } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Public sandbox demo for landing page — visitor uploads any house photo, we
 * run Gemini Vision, show what AI extracted. No DB writes, no auth. Social-
 * shareable so people can post their own house fingerprint.
 */
export default function TryFingerprintDemo() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const onFile = async (file) => {
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/public/property-fingerprint-demo`, {
        method: "POST", body: fd,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setResult(data.fingerprint || {});
    } catch (e) {
      toast.error("Demo scan failed — try again with a clearer photo");
    } finally { setBusy(false); }
  };

  const share = () => {
    const url = window.location.origin;
    if (navigator.share) {
      navigator.share({ title: "Field Radar", text: "AI just parsed my house 👀", url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied — share it around");
    }
  };

  return (
    <section id="try-fingerprint" className="py-16 sm:py-20 bg-gradient-to-b from-fuchsia-50 to-white"
      data-testid="landing-fp-demo">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-8">
          <div className="text-xs font-black uppercase tracking-widest mb-3 text-fuchsia-700">Try It Right Now</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight text-[#0b1220]">
            Snap any house · AI reads it
          </h2>
          <p className="mt-3 text-sm text-slate-600 max-w-xl mx-auto">
            The same Property Fingerprint your techs use in the field. No sign-up, nothing saved. Try your own place.
          </p>
        </div>

        <label className={`block p-6 border-4 border-dashed border-fuchsia-500 bg-white hover:bg-fuchsia-50 cursor-pointer text-center transition ${busy ? "opacity-60 cursor-wait" : ""}`}
          data-testid="landing-fp-label">
          <Camera weight="fill" size={40} className="mx-auto text-fuchsia-600"/>
          <div className="mt-2 font-display font-black uppercase tracking-widest text-[#0b1220]">
            {busy ? "AI is reading the building…" : "TAP TO UPLOAD A HOUSE PHOTO"}
          </div>
          <div className="text-[11px] text-slate-500">Front view · unit numbers help but aren't required</div>
          <input type="file" accept="image/*" hidden disabled={busy}
            data-testid="landing-fp-input"
            onChange={e => onFile(e.target.files?.[0])}/>
        </label>

        {previewUrl && (
          <div className="mt-6 flex gap-4 items-start flex-col sm:flex-row">
            <img src={previewUrl} alt="" className="w-full sm:w-56 max-h-56 object-cover border-2 border-slate-900"/>
            {result && (
              <div className="flex-1 p-4 bg-white border-4 border-[#0b1220] shadow-[6px_6px_0_#0b1220] text-sm space-y-1"
                data-testid="landing-fp-result">
                <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 mb-1 flex items-center gap-1">
                  <Sparkle weight="fill" size={12}/> AI extracted
                </div>
                <Row label="Number" value={result.street_number}/>
                <Row label="Street" value={result.street_name}/>
                <Row label="City" value={result.city}/>
                <Row label="Style" value={result.style}/>
                <Row label="Stories" value={result.stories}/>
                <Row label="Units guess" value={result.unit_count_guess}/>
                <Row label="Distinctive" value={result.distinguishing}/>
                <div className="pt-2 flex items-center gap-2 flex-wrap">
                  <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${
                    result.confidence === "high" ? "bg-emerald-600 text-white" :
                    result.confidence === "low" ? "bg-red-600 text-white" : "bg-amber-500 text-white"
                  }`}>Confidence {result.confidence || "?"}</span>
                  <button onClick={share} data-testid="landing-fp-share"
                    className="ml-auto h-8 px-3 border-2 border-[#0b1220] bg-white hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                    <Share weight="bold" size={12}/> Share Radar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between border-b border-slate-100 py-0.5">
    <span className="text-slate-500 text-[11px] uppercase tracking-widest font-black">{label}</span>
    <span className="font-bold text-right truncate ml-2 max-w-[60%] text-slate-800">{value || "—"}</span>
  </div>
);
